import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageSquare } from 'lucide-react';
import { Agent, ChatMessage, ChatSource, ChatActionConfirmation } from '../types';
import { useAgents } from '../contexts/AgentsContext';
import {
  ANONYMOUS_CURRENT_SESSION_HAS_MESSAGES_STORAGE_KEY,
  ANONYMOUS_CURRENT_SESSION_STORAGE_KEY,
  ANONYMOUS_PENDING_MERGE_SESSION_STORAGE_KEY,
  useAuth,
} from '../contexts/AuthContext';
import { useNotification } from '../hooks/useNotification';
import { useChatAgent } from '../hooks/useChatAgent';
import {
  getRealtimeVoicePublicErrorMessage,
  useRealtimeVoiceAgent,
} from '../hooks/useRealtimeVoiceAgent';
import { useStoredChatExecutionMode } from '../hooks/useStoredChatExecutionMode';
import { useQuotaBanner } from '../hooks/useQuotaBanner';
import { ChatConversation } from '../components/chat/ChatConversation';
import { AgentInfoPanel } from '../components/chat/AgentInfoPanel';
import { useSidebarConversations } from '../components/layout/useSidebarConversations';
import { CircularProgress } from '@mui/material';
import {
  cancelChatResponse,
  getChatSession,
  listChatMessages,
  getAgent,
  warmChatSessionRuntime,
  buildServerUrl,
  confirmToolAction,
} from '../services/api';
import { stripGeneratedImagePlaceholder } from '../utils/generatedImageMessage';
import {
  enrichLiveMessagesWithPersistedMetadata,
  getMessageDedupeKey,
  hasRenderableMessageContent,
  orderMessagesForDisplay,
  removePersistedMessagesDuplicatedByLiveMessages,
  shouldHideStaleProgressOnlyAgentMessage,
} from '../utils/chatMessageMerge';
import {
  buildPendingInitialMessage,
  getInitialMessageSessionId,
  getSeededInitialSession,
  getMatchingInitialAgent,
  getMatchingInitialSession,
  resolveRouteAgent,
  type ChatScreenInitialState,
} from './chatInitialRouteState';

const DEFAULT_SESSION_TITLES = new Set(['new chat', 'chat with ai assistant']);
const FIRST_RESPONSE_TITLE_SYNC_DELAYS_MS = [900, 2500];
const RESPONSE_METADATA_SYNC_DELAYS_MS = [1200];

const isDefaultConversationTitle = (title: string | null | undefined) => {
  const normalized = title?.trim().toLowerCase() ?? '';
  return (
    !normalized || DEFAULT_SESSION_TITLES.has(normalized) || normalized.startsWith('chat with ')
  );
};

export const ChatScreen: React.FC = () => {
  const { sessionId: routeSessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAnonymous, user, quota, refreshQuota } = useAuth();
  const { agents } = useAgents();
  const { error: showError } = useNotification();
  const { t } = useTranslation();
  const { sessions, addOrUpdateConversation } = useSidebarConversations();
  const [quotaErrorEvent, setQuotaErrorEvent] = useState<{
    code?: string;
    message?: string;
  } | null>(null);
  const initialSendRef = useRef<string | null>(null);
  const lastSyncedAgentMessageRef = useRef<string | null>(null);
  const lastSyncedResponseMetadataRef = useRef<string | null>(null);
  const lastAppliedConversationTitleRef = useRef<string | null>(null);
  const lastVoiceTranscriptSyncRef = useRef<string | null>(null);
  const initialRealtimeVoiceStartRef = useRef<string | null>(null);
  const lastChatErrorRef = useRef<string | null>(null);
  const messageLoadRequestRef = useRef(0);
  const initialState = location.state as ChatScreenInitialState | null;
  const initialStateRef = useRef<ChatScreenInitialState | null>(initialState);
  const skipInitialMessageLoadSessionRef = useRef(getInitialMessageSessionId(initialState));
  if (initialState?.initialSession && initialStateRef.current !== initialState) {
    initialStateRef.current = initialState;
    skipInitialMessageLoadSessionRef.current = getInitialMessageSessionId(initialState);
  }
  const initialMessage = initialState?.initialMessage;
  const initialFile = initialState?.initialFile ?? null;
  const initialStartRealtimeVoice = Boolean(initialState?.initialStartRealtimeVoice);
  const allowAnonymousSessionRef = useRef(Boolean(initialMessage));

  // Coming from /new-chat the session + agent are already known: render the conversation
  // immediately (no full-screen loader) and show the user's first message right away.
  const [seededSession] = useState(() => getSeededInitialSession(initialState, routeSessionId));
  const [agent, setAgent] = useState<Agent | null>(() =>
    seededSession
      ? resolveRouteAgent(
          getMatchingInitialAgent(initialState, seededSession.agentPublicId),
          seededSession,
          t('chat.fallbackAgentName'),
          t('chat.fallbackAgentDescription')
        )
      : null
  );
  const [activeSessionId, setActiveSessionId] = useState<string | null>(seededSession?.id ?? null);
  const [activeSessionInternalId, setActiveSessionInternalId] = useState<number | null>(
    seededSession?.internalId ?? null
  );
  const [isInfoPanelVisible, setIsInfoPanelVisible] = useState(false);
  const [isInitializing, setIsInitializing] = useState(!seededSession);
  const [initialMessagesLoaded, setInitialMessagesLoaded] = useState(Boolean(seededSession));
  const [isAwaitingResponse, setIsAwaitingResponse] = useState(false);
  const [cancelledResponseSessionId, setCancelledResponseSessionId] = useState<string | null>(null);
  const [executionMode, setExecutionMode] = useStoredChatExecutionMode(initialState?.initialMode);
  const [optimisticMessages, setOptimisticMessages] = useState<ChatMessage[]>([]);
  const optimisticAttachmentUrlsRef = useRef<Set<string>>(new Set());

  const {
    messages: agentMessages,
    sendMessage: agentSendMessage,
    isConnected,
    isConnecting,
    chatStatus,
    chatError,
    stop: stopAgentResponse,
  } = useChatAgent({
    conversationId: activeSessionId || '',
    agentPublicId: agent?.publicId || '',
    sessionId: activeSessionInternalId,
    mode: executionMode,
    enabled: !!agent && !!activeSessionId && activeSessionInternalId !== null,
  });

  const realtimeVoiceAgent = useRealtimeVoiceAgent({
    conversationId: activeSessionId || '',
    agentPublicId: agent?.publicId || '',
    sessionId: activeSessionInternalId,
    mode: executionMode,
    enabled: !isAnonymous && !!agent && !!activeSessionId && activeSessionInternalId !== null,
  });

  const [initialMessages, setInitialMessages] = useState<ChatMessage[]>([]);
  const [confirmationOverrides, setConfirmationOverrides] = useState<
    Record<string, ChatActionConfirmation['status']>
  >({});
  const revokeOptimisticMessageUrls = useCallback((messagesToRevoke: ChatMessage[]) => {
    for (const message of messagesToRevoke) {
      for (const attachment of message.attachments || []) {
        const url = attachment.fileUrl;
        if (url && optimisticAttachmentUrlsRef.current.has(url)) {
          URL.revokeObjectURL(url);
          optimisticAttachmentUrlsRef.current.delete(url);
        }
      }
    }
  }, []);
  const isActiveRouteSession = Boolean(routeSessionId && activeSessionId === routeSessionId);
  const realtimeVoiceMessages = useMemo(() => {
    if (!activeSessionId || !isActiveRouteSession || !realtimeVoiceAgent.enabled) {
      return [];
    }

    const transcriptMessages: ChatMessage[] = realtimeVoiceAgent.transcript
      .map((message, index): ChatMessage | null => {
        const content = message.text.trim();
        if (!content) {
          return null;
        }

        return {
          id: `voice-${message.role}-${message.timestamp}-${index}`,
          sessionId: activeSessionId,
          role: message.role === 'user' ? 'user' : 'agent',
          content,
          timestamp: new Date(message.timestamp),
          status: 'sent',
        };
      })
      .filter((message): message is ChatMessage => Boolean(message));

    const interimTranscript = realtimeVoiceAgent.interimTranscript?.trim();
    if (
      interimTranscript &&
      realtimeVoiceAgent.status === 'listening' &&
      transcriptMessages[transcriptMessages.length - 1]?.content !== interimTranscript
    ) {
      transcriptMessages.push({
        id: `voice-interim-${activeSessionId}`,
        sessionId: activeSessionId,
        role: 'user',
        content: interimTranscript,
        timestamp: new Date(),
        status: 'sending',
      });
    }

    return transcriptMessages;
  }, [
    activeSessionId,
    isActiveRouteSession,
    realtimeVoiceAgent.enabled,
    realtimeVoiceAgent.interimTranscript,
    realtimeVoiceAgent.status,
    realtimeVoiceAgent.transcript,
  ]);
  const pendingInitialFileUrl = useMemo(
    () => (initialFile ? URL.createObjectURL(initialFile) : null),
    [initialFile]
  );
  useEffect(
    () => () => {
      if (pendingInitialFileUrl) URL.revokeObjectURL(pendingInitialFileUrl);
    },
    [pendingInitialFileUrl]
  );
  const allMessages = useMemo(() => {
    if (!isActiveRouteSession || !initialMessagesLoaded) {
      return [];
    }

    // First message from /new-chat: show it before the socket connects and it is actually sent.
    const isInitialSendPending = Boolean(
      initialMessage?.trim() &&
      activeSessionId &&
      initialSendRef.current !== `${activeSessionId}\u0000${initialMessage}`
    );
    const pendingInitialMessages =
      isInitialSendPending && initialMessage && activeSessionId
        ? [
            buildPendingInitialMessage(
              activeSessionId,
              initialMessage,
              initialFile,
              pendingInitialFileUrl
            ),
          ]
        : [];

    const applyConfirmationOverrides = (messages: ChatMessage[]): ChatMessage[] => {
      if (Object.keys(confirmationOverrides).length === 0) return messages;
      return messages.map((msg) => {
        if (!msg.confirmation) return msg;
        const overrideStatus =
          confirmationOverrides[msg.confirmation.id] || (msg.id && confirmationOverrides[msg.id]);
        if (overrideStatus && msg.confirmation.status !== overrideStatus) {
          return {
            ...msg,
            confirmation: {
              ...msg.confirmation,
              status: overrideStatus,
            },
          };
        }
        return msg;
      });
    };

    const liveMessages = [...agentMessages, ...realtimeVoiceMessages];
    const visibleOptimisticMessages = removePersistedMessagesDuplicatedByLiveMessages(
      optimisticMessages,
      liveMessages
    );
    const liveAndOptimisticMessages = [
      ...pendingInitialMessages,
      ...visibleOptimisticMessages,
      ...liveMessages,
    ];

    if (liveAndOptimisticMessages.length === 0) {
      return orderMessagesForDisplay(applyConfirmationOverrides(initialMessages));
    }

    const dedupedInitialMessages = removePersistedMessagesDuplicatedByLiveMessages(
      initialMessages,
      liveAndOptimisticMessages
    );

    const enrichedAgentMessages = enrichLiveMessagesWithPersistedMetadata(
      initialMessages,
      liveMessages
    );

    return orderMessagesForDisplay(
      applyConfirmationOverrides([
        ...dedupedInitialMessages,
        ...pendingInitialMessages,
        ...visibleOptimisticMessages,
        ...enrichedAgentMessages,
      ])
    );
  }, [
    agentMessages,
    confirmationOverrides,
    initialMessages,
    initialMessagesLoaded,
    isActiveRouteSession,
    optimisticMessages,
    realtimeVoiceMessages,
    activeSessionId,
    initialFile,
    initialMessage,
    pendingInitialFileUrl,
  ]);
  const activeSession = useMemo(
    () => sessions.find((session) => session.id === activeSessionId) ?? null,
    [activeSessionId, sessions]
  );

  useEffect(() => {
    const handleQuotaExceededEvent = (event: Event) => {
      const detail = (
        event as CustomEvent<{ errorCode?: string; error?: string; message?: string }>
      ).detail;
      setQuotaErrorEvent({
        code: detail?.errorCode || detail?.error,
        message: detail?.message,
      });
      void refreshQuota();
    };

    window.addEventListener('quota:exceeded', handleQuotaExceededEvent);
    return () => {
      window.removeEventListener('quota:exceeded', handleQuotaExceededEvent);
    };
  }, [refreshQuota]);

  const effectiveLimitWarning = useQuotaBanner({
    sessionLimitWarning: activeSession?.limitWarning,
    isAnonymous,
    quota,
    quotaErrorEvent,
    fallbackEnabled:
      quota?.advanceFallbackEnabled ?? user?.setting?.useAdvanceTokensForChatFallback ?? false,
  });
  const visibleMessages = useMemo(
    () =>
      allMessages.filter((message, index) => {
        if (message.role !== 'agent') {
          return true;
        }

        if (!hasRenderableMessageContent(message)) {
          return false;
        }

        return !shouldHideStaleProgressOnlyAgentMessage(message, index, allMessages);
      }),
    [allMessages]
  );
  const latestVisibleMessage = visibleMessages[visibleMessages.length - 1];
  let lastUserMessageIndex = -1;
  for (let index = allMessages.length - 1; index >= 0; index -= 1) {
    if (allMessages[index].role === 'user') {
      lastUserMessageIndex = index;
      break;
    }
  }
  const responseMessagesAfterLastUser =
    lastUserMessageIndex >= 0 ? allMessages.slice(lastUserMessageIndex + 1) : allMessages;
  const latestResponseAgentMessage = responseMessagesAfterLastUser
    .filter((message) => message.role === 'agent')
    .slice(-1)[0];
  const latestResponseHasRenderableContent =
    latestResponseAgentMessage !== undefined &&
    hasRenderableMessageContent(latestResponseAgentMessage);
  const hasPendingAgentPlaceholder = responseMessagesAfterLastUser.some(
    (message) =>
      message.role === 'agent' &&
      message.status === 'sending' &&
      !hasRenderableMessageContent(message)
  );
  // Don't show the agent "thinking" while the user's own message still says "Sending...".
  const isLatestUserMessageSending =
    lastUserMessageIndex >= 0 && allMessages[lastUserMessageIndex].status === 'sending';
  const isResponseCancelled = cancelledResponseSessionId === activeSessionId;
  const isResponseInFlight =
    !isResponseCancelled &&
    (isAwaitingResponse || chatStatus === 'submitted' || chatStatus === 'streaming');
  const isRealtimeVoiceThinking =
    !isAnonymous &&
    (realtimeVoiceAgent.status === 'thinking' || realtimeVoiceAgent.status === 'speaking');
  const shouldShowThinkingIndicator =
    !isResponseCancelled &&
    (hasPendingAgentPlaceholder || isResponseInFlight || isRealtimeVoiceThinking) &&
    !latestResponseHasRenderableContent &&
    !isLatestUserMessageSending &&
    latestVisibleMessage?.role !== 'agent';
  const hasPendingInitialSend = Boolean(
    initialMessage?.trim() &&
    activeSessionId &&
    initialSendRef.current !== `${activeSessionId}\u0000${initialMessage}`
  );

  useEffect(() => {
    const latestAgentMessage = agentMessages[agentMessages.length - 1];
    if (
      !latestAgentMessage ||
      latestAgentMessage.role !== 'agent' ||
      !activeSessionId ||
      chatStatus !== 'ready'
    ) {
      return;
    }

    setIsAwaitingResponse(false);

    const currentSession = sessions.find((session) => session.id === activeSessionId);
    const conversationTitle = latestAgentMessage.conversationTitle?.trim();
    const userMessageCount = allMessages.filter((message) => message.role === 'user').length;
    const agentResponseCount = allMessages.filter(
      (message) =>
        message.role === 'agent' &&
        (message.content.trim().length > 0 || Boolean(message.reasoning?.trim()))
    ).length;
    const shouldSyncFirstResponseTitle = userMessageCount <= 1 && agentResponseCount <= 1;
    let didApplyConversationTitle = false;

    if (conversationTitle) {
      const titleKey = `${activeSessionId}\u0000${conversationTitle}`;

      if (
        lastAppliedConversationTitleRef.current !== titleKey &&
        currentSession &&
        isDefaultConversationTitle(currentSession.title)
      ) {
        lastAppliedConversationTitleRef.current = titleKey;
        addOrUpdateConversation({
          ...currentSession,
          title: conversationTitle,
          updatedAt: new Date(),
        });
        didApplyConversationTitle = true;
      }
    }

    if (
      didApplyConversationTitle ||
      (currentSession && !isDefaultConversationTitle(currentSession.title))
    ) {
      return;
    }

    if (!shouldSyncFirstResponseTitle) {
      return;
    }

    if (lastSyncedAgentMessageRef.current === latestAgentMessage.id) {
      return;
    }
    lastSyncedAgentMessageRef.current = latestAgentMessage.id;

    let cancelled = false;
    void (async () => {
      for (const delayMs of FIRST_RESPONSE_TITLE_SYNC_DELAYS_MS) {
        if (cancelled) {
          return;
        }

        if (delayMs > 0) {
          await new Promise((resolve) => window.setTimeout(resolve, delayMs));
        }

        if (cancelled) {
          return;
        }

        try {
          const response = await getChatSession(activeSessionId);
          if (!response.success || !response.data || cancelled) {
            continue;
          }

          if (isDefaultConversationTitle(response.data.title)) {
            continue;
          }

          addOrUpdateConversation(response.data);
          return;
        } catch {
          // Session title sync is non-critical; a later sidebar refresh will pick it up.
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeSessionId, addOrUpdateConversation, agentMessages, allMessages, chatStatus, sessions]);

  // Reset thinking indicator when switching sessions
  useEffect(() => {
    setIsAwaitingResponse(false);
    setCancelledResponseSessionId(null);
    lastSyncedAgentMessageRef.current = null;
    lastSyncedResponseMetadataRef.current = null;
    lastAppliedConversationTitleRef.current = null;
    setOptimisticMessages((currentMessages) => {
      revokeOptimisticMessageUrls(currentMessages);
      return [];
    });
  }, [activeSessionId, revokeOptimisticMessageUrls]);

  useEffect(() => {
    const optimisticAttachmentUrls = optimisticAttachmentUrlsRef.current;
    return () => {
      for (const url of optimisticAttachmentUrls) {
        URL.revokeObjectURL(url);
      }
      optimisticAttachmentUrls.clear();
    };
  }, []);

  // The request has left the client once the chat transport is submitted/streaming.
  useEffect(() => {
    if (chatStatus !== 'submitted' && chatStatus !== 'streaming') {
      return;
    }

    setOptimisticMessages((currentMessages) => {
      if (!currentMessages.some((message) => message.status === 'sending')) {
        return currentMessages;
      }

      return currentMessages.map((message) =>
        message.status === 'sending' ? { ...message, status: 'sent' as const } : message
      );
    });
  }, [chatStatus, optimisticMessages]);

  useEffect(() => {
    if (optimisticMessages.length === 0 || agentMessages.length === 0) {
      return;
    }

    const liveUserMessageCounts = new Map<string, number>();
    for (const message of agentMessages) {
      if (message.role !== 'user') continue;
      const key = getMessageDedupeKey(message);
      liveUserMessageCounts.set(key, (liveUserMessageCounts.get(key) ?? 0) + 1);
    }

    setOptimisticMessages((currentMessages) => {
      const removedMessages: ChatMessage[] = [];
      const nextMessages = currentMessages.filter((message) => {
        const key = getMessageDedupeKey(message);
        const liveCount = liveUserMessageCounts.get(key) ?? 0;
        if (liveCount <= 0) {
          return true;
        }

        liveUserMessageCounts.set(key, liveCount - 1);
        removedMessages.push(message);
        return false;
      });

      if (removedMessages.length === 0) {
        return currentMessages;
      }

      revokeOptimisticMessageUrls(removedMessages);
      return nextMessages;
    });
  }, [agentMessages, optimisticMessages.length, revokeOptimisticMessageUrls]);

  useEffect(() => {
    if (!routeSessionId || routeSessionId === activeSessionId) {
      return;
    }

    messageLoadRequestRef.current += 1;
    setInitialMessages([]);
    setInitialMessagesLoaded(false);
    setConfirmationOverrides({});
    setIsAwaitingResponse(false);
  }, [activeSessionId, routeSessionId]);

  // ============================================
  // Initialize Agent & Sessions
  // ============================================

  useEffect(() => {
    let cancelled = false;

    const initializeChatScreen = async () => {
      if (activeSessionId !== routeSessionId) {
        setIsInitializing(true);
      }

      if (!routeSessionId) {
        showError(t('chat.errors.sessionIdRequired'));
        navigate('/new-chat');
        return;
      }

      if (isAnonymous && !allowAnonymousSessionRef.current) {
        navigate('/', { replace: true });
        return;
      }

      if (
        !isAnonymous &&
        window.sessionStorage.getItem(ANONYMOUS_PENDING_MERGE_SESSION_STORAGE_KEY) ===
          routeSessionId
      ) {
        navigate('/new-chat', { replace: true });
        return;
      }

      const stateSession = getMatchingInitialSession(initialStateRef.current, routeSessionId);
      const sessionResponse = stateSession ? null : await getChatSession(routeSessionId);
      if (cancelled) return;
      if (sessionResponse && (!sessionResponse.success || !sessionResponse.data)) {
        const sessionError =
          sessionResponse.errorCode === 'ACCESS_DENIED' ||
          sessionResponse.errorCode === 'SESSION_NOT_FOUND'
            ? t('chat.errors.sessionNotFound')
            : sessionResponse.error || t('chat.errors.sessionNotFound');
        showError(sessionError);
        navigate('/new-chat');
        return;
      }
      const currentSession = stateSession ?? sessionResponse?.data;
      if (!currentSession) {
        navigate('/new-chat');
        return;
      }
      if (typeof currentSession.internalId !== 'number') {
        showError(t('chat.errors.missingInternalId'));
        navigate('/new-chat');
        return;
      }

      if (!currentSession.agentPublicId) {
        showError(t('chat.errors.missingAgentId'));
        navigate('/new-chat');
        return;
      }

      const stateAgent = getMatchingInitialAgent(
        initialStateRef.current,
        currentSession.agentPublicId
      );
      const contextAgent = agents.find((item) => item.publicId === currentSession.agentPublicId);
      const agentResponse =
        stateAgent || contextAgent || isAnonymous
          ? null
          : await getAgent(currentSession.agentPublicId);
      if (cancelled) {
        return;
      }

      const loadedAgent =
        stateAgent ||
        contextAgent ||
        (agentResponse?.success && agentResponse.data ? agentResponse.data : null);
      const resolvedAgent = resolveRouteAgent(
        loadedAgent,
        currentSession,
        t('chat.fallbackAgentName'),
        t('chat.fallbackAgentDescription')
      );

      void warmChatSessionRuntime(currentSession.id, {
        mode: executionMode,
        agentPublicId: currentSession.agentPublicId,
      });

      setAgent(resolvedAgent);
      addOrUpdateConversation(currentSession);

      setActiveSessionId(currentSession.id);
      setActiveSessionInternalId(currentSession.internalId);
      setIsInitializing(false);
    };

    initializeChatScreen();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeSessionId, agents, isAnonymous]);

  useEffect(() => {
    if (!isAnonymous || !activeSessionId) {
      return;
    }

    const hasMessagesToMerge =
      visibleMessages.length > 0 ||
      Boolean(initialMessage?.trim()) ||
      Boolean(initialFile) ||
      isAwaitingResponse;

    window.sessionStorage.setItem(ANONYMOUS_CURRENT_SESSION_STORAGE_KEY, activeSessionId);
    window.sessionStorage.setItem(
      ANONYMOUS_CURRENT_SESSION_HAS_MESSAGES_STORAGE_KEY,
      String(hasMessagesToMerge)
    );
  }, [
    activeSessionId,
    initialFile,
    initialMessage,
    isAnonymous,
    isAwaitingResponse,
    visibleMessages.length,
  ]);

  // ============================================
  // Helper Functions
  // ============================================

  const extractSourcesFromRawPayload = useCallback(
    (rawPayload: string | Record<string, unknown> | null | undefined): ChatSource[] => {
      if (!rawPayload) return [];

      try {
        const payload =
          typeof rawPayload === 'string'
            ? (JSON.parse(rawPayload) as Record<string, unknown>)
            : rawPayload;
        const sources = payload.sources;
        if (!Array.isArray(sources)) return [];

        return sources
          .map((source): ChatSource | null => {
            if (!source || typeof source !== 'object') return null;
            const item = source as Record<string, unknown>;
            if (typeof item.marker !== 'string' || typeof item.title !== 'string') return null;
            return {
              marker: item.marker,
              title: item.title,
              url: typeof item.url === 'string' ? item.url : undefined,
              section: typeof item.section === 'string' ? item.section : undefined,
              excerpt: typeof item.excerpt === 'string' ? item.excerpt : undefined,
            };
          })
          .filter((source): source is ChatSource => Boolean(source));
      } catch {
        return [];
      }
    },
    []
  );

  const extractReasoningFromRawPayload = useCallback(
    (rawPayload: string | Record<string, unknown> | null | undefined): string | null => {
      if (!rawPayload) return null;

      try {
        const payload =
          typeof rawPayload === 'string'
            ? (JSON.parse(rawPayload) as Record<string, unknown>)
            : rawPayload;
        const reasoning = payload.reasoning;
        return typeof reasoning === 'string' && reasoning.trim() ? reasoning : null;
      } catch {
        return null;
      }
    },
    []
  );

  const isCancelledRawPayload = useCallback(
    (rawPayload: string | Record<string, unknown> | null | undefined): boolean => {
      if (!rawPayload) return false;

      try {
        const payload =
          typeof rawPayload === 'string'
            ? (JSON.parse(rawPayload) as Record<string, unknown>)
            : rawPayload;
        return payload.cancelled === true || payload.reason === 'user_cancelled';
      } catch {
        return false;
      }
    },
    []
  );

  const extractAttachmentsFromRawPayload = useCallback(
    (
      rawPayload: string | Record<string, unknown> | null | undefined,
      existingAttachments: NonNullable<ChatMessage['attachments']> = []
    ): NonNullable<ChatMessage['attachments']> => {
      if (!rawPayload) return [];

      try {
        const payload =
          typeof rawPayload === 'string'
            ? (JSON.parse(rawPayload) as Record<string, unknown>)
            : rawPayload;
        const files = payload.generatedFiles;
        if (!Array.isArray(files)) return [];

        const existingKeys = new Set<string>();
        for (const att of existingAttachments) {
          if (att.publicId) existingKeys.add(att.publicId);
          if (att.fileUrl) existingKeys.add(att.fileUrl);
        }

        const attachments: NonNullable<ChatMessage['attachments']> = [];
        for (const [index, file] of files.entries()) {
          if (!file || typeof file !== 'object') continue;
          const item = file as Record<string, unknown>;
          const filePublicId =
            (typeof item.publicId === 'string' && item.publicId.trim()) ||
            (typeof item.id === 'string' && item.id.trim()) ||
            '';
          const attachmentPublicId =
            typeof item.attachmentPublicId === 'string' && item.attachmentPublicId.trim()
              ? item.attachmentPublicId.trim()
              : '';
          const publicId = attachmentPublicId || filePublicId || `generated-history-${index}`;
          const fileName =
            (typeof item.fileName === 'string' && item.fileName.trim()) ||
            (typeof item.name === 'string' && item.name.trim()) ||
            '';
          const mimeType =
            (typeof item.mimeType === 'string' && item.mimeType.trim()) ||
            (typeof item.mime_type === 'string' && item.mime_type.trim()) ||
            'application/octet-stream';
          const fileSize =
            typeof item.fileSize === 'number'
              ? item.fileSize
              : typeof item.size === 'number'
                ? item.size
                : 0;
          const rawDownloadUrl =
            (typeof item.downloadUrl === 'string' && item.downloadUrl.trim()) ||
            (typeof item.download_url === 'string' && item.download_url.trim()) ||
            '';
          const hasExplicitId = Boolean(filePublicId) || Boolean(attachmentPublicId);
          const downloadUrl =
            rawDownloadUrl || (filePublicId ? `/v1/files/${filePublicId}/download` : '');

          if (!fileName || !downloadUrl || !hasExplicitId) continue;
          const resolvedUrl = buildServerUrl(downloadUrl);
          if (
            existingKeys.has(publicId) ||
            existingKeys.has(downloadUrl) ||
            existingKeys.has(resolvedUrl)
          ) {
            continue;
          }
          existingKeys.add(publicId);
          existingKeys.add(downloadUrl);
          existingKeys.add(resolvedUrl);

          attachments.push({
            publicId,
            fileName,
            mimeType,
            fileSize,
            fileUrl: resolvedUrl,
          });
        }
        return attachments;
      } catch {
        return [];
      }
    },
    []
  );

  const extractDiagramsFromRawPayload = useCallback(
    (
      rawPayload: string | Record<string, unknown> | null | undefined
    ): NonNullable<ChatMessage['diagrams']> => {
      if (!rawPayload) return [];

      try {
        const payload =
          typeof rawPayload === 'string'
            ? (JSON.parse(rawPayload) as Record<string, unknown>)
            : rawPayload;
        const diagrams = payload.generatedDiagrams;
        if (!Array.isArray(diagrams)) return [];

        const existingIds = new Set<string>();
        const list: NonNullable<ChatMessage['diagrams']> = [];

        for (const [index, item] of diagrams.entries()) {
          if (!item || typeof item !== 'object') continue;
          const record = item as Record<string, unknown>;
          const id = (typeof record.id === 'string' && record.id.trim()) || `history-diag-${index}`;
          const diagramType =
            (typeof record.diagramType === 'string' && record.diagramType.trim()) ||
            (typeof record.diagram_type === 'string' && record.diagram_type.trim()) ||
            'flowchart';
          const mermaid =
            (typeof record.mermaid === 'string' && record.mermaid.trim()) ||
            (typeof record.content === 'string' && record.content.trim()) ||
            '';
          const title = (typeof record.title === 'string' && record.title.trim()) || undefined;
          const description =
            (typeof record.description === 'string' && record.description.trim()) || undefined;

          if (!mermaid || existingIds.has(id)) continue;
          existingIds.add(id);

          list.push({
            id,
            title,
            diagramType,
            mermaid,
            description,
          });
        }
        return list;
      } catch {
        return [];
      }
    },
    []
  );

  const extractConfirmationFromRawPayload = useCallback(
    (
      rawPayload: string | Record<string, unknown> | null | undefined,
      messageId?: string
    ): ChatActionConfirmation | undefined => {
      if (!rawPayload) return undefined;

      try {
        const payload =
          typeof rawPayload === 'string'
            ? (JSON.parse(rawPayload) as Record<string, unknown>)
            : rawPayload;
        const conf =
          payload.confirmation || payload.pendingConfirmation || payload.actionConfirmation;
        if (!conf || typeof conf !== 'object' || Array.isArray(conf)) return undefined;

        const item = conf as Record<string, unknown>;
        const id = (typeof item.id === 'string' && item.id.trim()) || `conf-${Date.now()}`;
        const toolPublicId =
          (typeof item.toolPublicId === 'string' && item.toolPublicId.trim()) ||
          (typeof item.tool_public_id === 'string' && item.tool_public_id.trim()) ||
          undefined;
        const toolName =
          (typeof item.toolName === 'string' && item.toolName.trim()) ||
          (typeof item.tool_name === 'string' && item.tool_name.trim()) ||
          'external_tool';
        const actionTitle =
          typeof item.actionTitle === 'string'
            ? item.actionTitle
            : typeof item.action_title === 'string'
              ? item.action_title
              : undefined;
        const summary = typeof item.summary === 'string' ? item.summary : undefined;
        const riskLevel = ((typeof item.riskLevel === 'string' && item.riskLevel) ||
          (typeof item.risk_level === 'string' && item.risk_level) ||
          'write') as ChatActionConfirmation['riskLevel'];
        const endpointUrl =
          typeof item.endpointUrl === 'string'
            ? item.endpointUrl
            : typeof item.endpoint_url === 'string'
              ? item.endpoint_url
              : undefined;
        const parameters = (
          item.parameters && typeof item.parameters === 'object' && !Array.isArray(item.parameters)
            ? item.parameters
            : {}
        ) as Record<string, unknown>;
        const idempotencyKey =
          typeof item.idempotencyKey === 'string'
            ? item.idempotencyKey
            : typeof item.idempotency_key === 'string'
              ? item.idempotency_key
              : undefined;
        const status = ((typeof item.status === 'string' && item.status) ||
          'pending') as ChatActionConfirmation['status'];

        return {
          id,
          toolPublicId,
          toolName,
          actionTitle,
          summary,
          riskLevel,
          endpointUrl,
          parameters,
          idempotencyKey,
          status,
          messageId,
        };
      } catch {
        return undefined;
      }
    },
    []
  );

  const normalizeMessageStatus = useCallback((status: unknown): ChatMessage['status'] => {
    if (typeof status !== 'string') return 'sent';

    switch (status.toUpperCase()) {
      case 'PENDING':
      case 'STREAMING':
        return 'sending';
      case 'FAILED':
        return 'failed';
      default:
        return 'sent';
    }
  }, []);

  const loadInitialMessages = useCallback(
    async (sessionId: string, options?: { resetLoaded?: boolean }) => {
      const requestId = ++messageLoadRequestRef.current;

      if (options?.resetLoaded !== false) {
        setInitialMessages([]);
        setInitialMessagesLoaded(false);
        setConfirmationOverrides({});
      }
      const response = await listChatMessages(sessionId);

      if (requestId !== messageLoadRequestRef.current) {
        return;
      }

      if (response.success && response.data) {
        // Convert backend message format to UI format
        const formattedMessages: ChatMessage[] = response.data.map((message) => {
          const msg = message as unknown as {
            publicId?: string;
            id?: string;
            sessionId?: string;
            role: string;
            content: string;
            timestamp?: string | Date;
            createdAt?: string | Date;
            status?: ChatMessage['status'];
            reasoning?: string | null;
            sources?: ChatSource[];
            attachments?: ChatMessage['attachments'];
            rawPayload?: string | Record<string, unknown> | null;
          };
          const status = normalizeMessageStatus(msg.status);
          const isCancelled = isCancelledRawPayload(msg.rawPayload);
          const rawAttachments = Array.isArray(msg.attachments) ? msg.attachments : [];
          const normalizedExisting = rawAttachments.map((att) => ({
            ...att,
            fileUrl: att.fileUrl ? buildServerUrl(att.fileUrl) : att.fileUrl,
          }));
          const fallbackAttachments = extractAttachmentsFromRawPayload(
            msg.rawPayload,
            normalizedExisting
          );
          const attachments = [...normalizedExisting, ...fallbackAttachments];
          const diagrams = extractDiagramsFromRawPayload(msg.rawPayload);
          const confirmation = extractConfirmationFromRawPayload(
            msg.rawPayload,
            msg.publicId || msg.id
          );

          return {
            id: msg.publicId || msg.id || `${msg.role}-${Date.now()}`,
            sessionId: msg.sessionId || sessionId,
            role: msg.role === 'user' ? 'user' : 'agent',
            content:
              msg.role === 'user'
                ? msg.content
                : stripGeneratedImagePlaceholder(msg.content, attachments),
            reasoning: msg.reasoning || extractReasoningFromRawPayload(msg.rawPayload),
            sources: msg.sources || extractSourcesFromRawPayload(msg.rawPayload),
            attachments,
            diagrams,
            confirmation,
            timestamp: new Date(msg.timestamp || msg.createdAt || Date.now()),
            status: isCancelled ? 'sent' : status,
          };
        });
        setInitialMessages(formattedMessages);
        setInitialMessagesLoaded(true);
      } else {
        setInitialMessages([]);
        setInitialMessagesLoaded(true);
      }
    },
    [
      extractAttachmentsFromRawPayload,
      extractDiagramsFromRawPayload,
      extractConfirmationFromRawPayload,
      extractReasoningFromRawPayload,
      extractSourcesFromRawPayload,
      isCancelledRawPayload,
      normalizeMessageStatus,
    ]
  );

  useEffect(() => {
    if (activeSessionId) {
      if (skipInitialMessageLoadSessionRef.current === activeSessionId) {
        skipInitialMessageLoadSessionRef.current = null;
        messageLoadRequestRef.current += 1;
        setInitialMessages([]);
        setInitialMessagesLoaded(true);
        setConfirmationOverrides({});
        return;
      }
      loadInitialMessages(activeSessionId);
    }
  }, [activeSessionId, loadInitialMessages]);

  useEffect(() => {
    if (!activeSessionId || !agent?.publicId) {
      return;
    }

    void warmChatSessionRuntime(activeSessionId, {
      mode: executionMode,
      agentPublicId: agent.publicId,
    });
  }, [activeSessionId, agent?.publicId, executionMode]);

  useEffect(() => {
    if (!activeSessionId || realtimeVoiceAgent.transcript.length === 0) {
      return;
    }

    const latestTranscript =
      realtimeVoiceAgent.transcript[realtimeVoiceAgent.transcript.length - 1];
    if (!latestTranscript) {
      return;
    }

    const syncKey = `${activeSessionId}\u0000${realtimeVoiceAgent.transcript.length}\u0000${latestTranscript.timestamp}`;
    if (lastVoiceTranscriptSyncRef.current === syncKey) {
      return;
    }
    lastVoiceTranscriptSyncRef.current = syncKey;

    const timeoutId = window.setTimeout(() => {
      void loadInitialMessages(activeSessionId, { resetLoaded: false });
    }, 800);

    return () => window.clearTimeout(timeoutId);
  }, [activeSessionId, loadInitialMessages, realtimeVoiceAgent.transcript]);

  useEffect(() => {
    if (!activeSessionId || chatStatus !== 'ready') {
      return;
    }

    const latestAgentMessage = [...agentMessages]
      .reverse()
      .find((message) => message.role === 'agent' && hasRenderableMessageContent(message));

    if (!latestAgentMessage) {
      return;
    }

    const syncKey = `${activeSessionId}\u0000${latestAgentMessage.id}`;
    if (lastSyncedResponseMetadataRef.current === syncKey) {
      return;
    }
    lastSyncedResponseMetadataRef.current = syncKey;

    let cancelled = false;
    void (async () => {
      for (const delayMs of RESPONSE_METADATA_SYNC_DELAYS_MS) {
        await new Promise((resolve) => window.setTimeout(resolve, delayMs));

        if (cancelled) {
          return;
        }

        await loadInitialMessages(activeSessionId, { resetLoaded: false });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeSessionId, agentMessages, chatStatus, loadInitialMessages]);

  useEffect(() => {
    if (!chatError) {
      return;
    }

    const errorKey = `${activeSessionId ?? 'none'}:${chatError.message}`;
    if (lastChatErrorRef.current === errorKey) {
      return;
    }

    lastChatErrorRef.current = errorKey;
    setIsAwaitingResponse(false);

    const isQuota =
      chatError.message?.toLowerCase().includes('quota') ||
      chatError.message?.toLowerCase().includes('token limit');

    if (isQuota) {
      setQuotaErrorEvent({
        code: 'QUOTA_EXCEEDED',
        message: chatError.message,
      });
      void refreshQuota();
      return;
    }

    showError(chatError.message || t('chat.errors.sendFailed'));
  }, [activeSessionId, chatError, refreshQuota, showError, t]);

  useEffect(() => {
    if (!activeSessionId) return;

    const hasPendingPersistedResponse = initialMessages.some(
      (message) => message.role === 'agent' && message.status === 'sending'
    );
    if (!hasPendingPersistedResponse) return;

    const intervalId = window.setInterval(() => {
      void loadInitialMessages(activeSessionId, { resetLoaded: false });
    }, 2000);

    return () => window.clearInterval(intervalId);
  }, [activeSessionId, initialMessages, loadInitialMessages]);

  // ============================================
  // Handlers
  // ============================================

  const handleSendMessage = useCallback(
    async (content: string, files?: File[]) => {
      if (!content.trim() && (!files || files.length === 0)) {
        return;
      }

      setQuotaErrorEvent(null);

      if (!isConnected || !activeSessionId) {
        showError(t('chat.errors.notConnected'));
        return;
      }

      const optimisticId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const optimisticAttachments = (files || []).map((file, index) => {
        const fileUrl = URL.createObjectURL(file);
        optimisticAttachmentUrlsRef.current.add(fileUrl);

        return {
          publicId: `${optimisticId}-attachment-${index}`,
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          fileSize: file.size,
          fileUrl,
        };
      });
      const optimisticMessage: ChatMessage = {
        id: optimisticId,
        sessionId: activeSessionId,
        role: 'user',
        content,
        attachments: optimisticAttachments,
        timestamp: new Date(),
        status: 'sending',
      };

      setOptimisticMessages((currentMessages) => [...currentMessages, optimisticMessage]);
      setIsAwaitingResponse(true);
      setCancelledResponseSessionId(null);

      try {
        await agentSendMessage(content, files);

        const currentSession = sessions.find((s) => s.id === activeSessionId);

        if (currentSession) {
          addOrUpdateConversation({
            ...currentSession,
            lastMessage: content.substring(0, 50),
            lastMessageAt: new Date(),
            updatedAt: new Date(),
          });
        }
      } catch (err) {
        setIsAwaitingResponse(false);
        setOptimisticMessages((currentMessages) =>
          currentMessages.map((message) =>
            message.id === optimisticId ? { ...message, status: 'failed' as const } : message
          )
        );
        const error = err instanceof Error ? err : new Error(t('chat.errors.sendFailed'));
        showError(error.message);
        throw error;
      }
    },
    [
      isConnected,
      activeSessionId,
      agentSendMessage,
      showError,
      sessions,
      addOrUpdateConversation,
      t,
    ]
  );

  const handleCancelResponse = useCallback(async () => {
    if (!activeSessionId) {
      return;
    }

    const partialResponseSnapshot = [...responseMessagesAfterLastUser]
      .reverse()
      .find((message) => message.role === 'agent' && hasRenderableMessageContent(message));

    try {
      stopAgentResponse();
    } catch {
      // The persisted cancel below is still enough to unblock this conversation.
    }

    setIsAwaitingResponse(false);
    setCancelledResponseSessionId(activeSessionId);
    setInitialMessages((currentMessages) => {
      const nextMessages = currentMessages.flatMap((message) => {
        if (message.role !== 'agent' || message.status !== 'sending') {
          return [message];
        }

        if (!hasRenderableMessageContent(message)) {
          return [];
        }

        return [{ ...message, status: 'sent' as const }];
      });

      if (!partialResponseSnapshot) {
        return nextMessages;
      }

      const snapshot = { ...partialResponseSnapshot, status: 'sent' as const };
      const existingIndex = nextMessages.findIndex((message) => message.id === snapshot.id);
      if (existingIndex >= 0) {
        return nextMessages.map((message, index) => (index === existingIndex ? snapshot : message));
      }

      return [...nextMessages, snapshot];
    });

    await new Promise((resolve) => window.setTimeout(resolve, 300));

    const response = await cancelChatResponse(
      activeSessionId,
      partialResponseSnapshot
        ? {
            content: partialResponseSnapshot.content,
            reasoning: partialResponseSnapshot.reasoning ?? null,
          }
        : undefined
    );
    if (!response.success) {
      showError(response.error || t('chat.errors.cancelFailed'));
      return;
    }

    await new Promise((resolve) => window.setTimeout(resolve, 150));
    await loadInitialMessages(activeSessionId, { resetLoaded: false });
  }, [
    activeSessionId,
    loadInitialMessages,
    responseMessagesAfterLastUser,
    showError,
    stopAgentResponse,
    t,
  ]);

  const handleConfirmAction = useCallback(
    async (confirmation: ChatActionConfirmation) => {
      const key =
        confirmation.idempotencyKey ||
        confirmation.id ||
        (confirmation.messageId ? `conf-${confirmation.messageId}` : undefined);

      // Execute structured confirmation call against backend
      // Send tool_public_id, message_id, session_id for strict backend verification and persistence.
      // Do not send client-supplied endpoint_url to prevent arbitrary URL execution.
      const res = await confirmToolAction({
        action_id: confirmation.id,
        tool_public_id: confirmation.toolPublicId,
        tool_name: confirmation.toolName,
        decision: 'confirm',
        idempotency_key: key,
        parameters: confirmation.parameters,
        message_id: confirmation.messageId,
        session_id: activeSessionId || undefined,
      });

      if (!res.success) {
        const errText = res.error || 'Thao tác xác nhận thất bại';
        setConfirmationOverrides((prev) => ({
          ...prev,
          [confirmation.id]: 'failed',
          ...(confirmation.messageId ? { [confirmation.messageId]: 'failed' } : {}),
        }));
        setInitialMessages((prev) =>
          prev.map((msg) =>
            msg.id === confirmation.messageId || msg.confirmation?.id === confirmation.id
              ? {
                  ...msg,
                  confirmation: {
                    ...msg.confirmation!,
                    status: 'failed',
                  },
                }
              : msg
          )
        );
        showError(errText);
        throw new Error(errText);
      }

      setConfirmationOverrides((prev) => ({
        ...prev,
        [confirmation.id]: 'confirmed',
        ...(confirmation.messageId ? { [confirmation.messageId]: 'confirmed' } : {}),
      }));
      setInitialMessages((prev) =>
        prev.map((msg) =>
          msg.id === confirmation.messageId || msg.confirmation?.id === confirmation.id
            ? {
                ...msg,
                confirmation: {
                  ...msg.confirmation!,
                  status: 'confirmed',
                },
              }
            : msg
        )
      );

      // Record confirmed execution in chat history
      const resultData = res.data?.data;
      const resultPreview =
        typeof resultData === 'object'
          ? JSON.stringify(resultData)
          : String(resultData ?? 'Thành công');

      await handleSendMessage(
        `Đã xác nhận thực thi hành động "${confirmation.actionTitle || confirmation.toolName}". Kết quả: ${resultPreview}`
      );
    },
    [activeSessionId, handleSendMessage, showError]
  );

  const handleCancelAction = useCallback(
    async (confirmation: ChatActionConfirmation) => {
      setConfirmationOverrides((prev) => ({
        ...prev,
        [confirmation.id]: 'cancelled',
        ...(confirmation.messageId ? { [confirmation.messageId]: 'cancelled' } : {}),
      }));
      setInitialMessages((prev) =>
        prev.map((msg) =>
          msg.id === confirmation.messageId || msg.confirmation?.id === confirmation.id
            ? {
                ...msg,
                confirmation: {
                  ...msg.confirmation!,
                  status: 'cancelled',
                },
              }
            : msg
        )
      );

      await confirmToolAction({
        action_id: confirmation.id,
        tool_public_id: confirmation.toolPublicId,
        tool_name: confirmation.toolName,
        decision: 'cancel',
        message_id: confirmation.messageId,
        session_id: activeSessionId || undefined,
      }).catch((err) => {
        console.warn('Cancel confirmation error:', err);
      });

      await handleSendMessage(
        `Đã hủy bỏ hành động "${confirmation.actionTitle || confirmation.toolName}".`
      );
    },
    [activeSessionId, handleSendMessage]
  );

  useEffect(() => {
    if (!initialMessage || !isConnected || !activeSessionId) {
      return;
    }

    const initialSendKey = `${activeSessionId}\u0000${initialMessage}`;
    if (initialSendRef.current === initialSendKey) {
      return;
    }

    initialSendRef.current = initialSendKey;
    void handleSendMessage(initialMessage, initialFile ? [initialFile] : undefined).catch(() => {
      // Error is already surfaced by handleSendMessage.
    });
    navigate(location.pathname, { replace: true, state: null });
  }, [
    activeSessionId,
    handleSendMessage,
    initialFile,
    initialMessage,
    isConnected,
    location.pathname,
    navigate,
  ]);

  useEffect(() => {
    if (
      !initialStartRealtimeVoice ||
      isAnonymous ||
      !activeSessionId ||
      !realtimeVoiceAgent.available ||
      !realtimeVoiceAgent.connected ||
      realtimeVoiceAgent.status !== 'idle'
    ) {
      return;
    }

    const startKey = `${activeSessionId}\u0000voice`;
    if (initialRealtimeVoiceStartRef.current === startKey) {
      return;
    }

    initialRealtimeVoiceStartRef.current = startKey;
    void realtimeVoiceAgent.startCall().catch((error) => {
      showError(getRealtimeVoicePublicErrorMessage(error));
    });
    navigate(location.pathname, { replace: true, state: null });
  }, [
    activeSessionId,
    initialStartRealtimeVoice,
    isAnonymous,
    location.pathname,
    navigate,
    realtimeVoiceAgent,
    showError,
  ]);

  const handleToggleInfo = useCallback(() => {
    setIsInfoPanelVisible((prev) => !prev);
  }, []);

  // ============================================
  // Render
  // ============================================

  // Show loading while chat is initializing
  if (isInitializing || !isActiveRouteSession || !initialMessagesLoaded) {
    return (
      <div className="flex h-screen items-center justify-center bg-white dark:bg-slate-900">
        <div className="text-center">
          <CircularProgress size={40} className="mb-4" />
          <p className="text-gray-600 dark:text-slate-400">{t('chat.loading')}</p>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="flex h-screen items-center justify-center bg-white dark:bg-slate-900">
        <p className="text-gray-600 dark:text-slate-400">{t('chat.errors.agentNotFound')}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 overflow-hidden">
      {/* Center - Chat Conversation or Empty State */}
      {activeSessionId ? (
        <ChatConversation
          agent={agent}
          messages={visibleMessages}
          isLoading={
            shouldShowThinkingIndicator || (hasPendingInitialSend && visibleMessages.length === 0)
          }
          isGenerating={isResponseInFlight || hasPendingInitialSend}
          isInputDisabled={isConnecting}
          onSendMessage={handleSendMessage}
          onCancelResponse={isResponseInFlight ? handleCancelResponse : undefined}
          onConfirmAction={handleConfirmAction}
          onCancelAction={handleCancelAction}
          executionMode={executionMode}
          onExecutionModeChange={setExecutionMode}
          voiceInputEnabled={!isAnonymous}
          voiceAgent={isAnonymous ? undefined : realtimeVoiceAgent}
          onToggleInfo={handleToggleInfo}
          userAvatar={isAnonymous ? undefined : user?.avatar}
          userAvatarType={isAnonymous ? undefined : user?.avatarType}
          sessionLimitWarning={effectiveLimitWarning}
        />
      ) : (
        <div className="ambient-surface flex h-full flex-1 flex-col items-center justify-center bg-gray-50 dark:bg-slate-900">
          <div className="text-center">
            <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-slate-800">
              <MessageSquare size={32} className="text-gray-400 dark:text-slate-500" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-slate-100">
              {t('chat.empty.title')}
            </h3>
            <p className="max-w-md text-sm text-gray-600 dark:text-slate-400">
              {t('chat.empty.prefix')}{' '}
              <span className="font-medium text-blue-500">{t('sidebar.newChat')}</span>{' '}
              {t('chat.empty.suffix', { name: agent.name })}
            </p>
          </div>
        </div>
      )}

      {/* WebSocket Status Indicator */}
      {!isConnected && activeSessionId && (
        <div className="fixed bottom-4 right-4 rounded-lg bg-yellow-100 px-4 py-2 text-sm text-yellow-800 shadow-lg dark:bg-yellow-900 dark:text-yellow-200">
          {isConnecting ? t('chat.connection.connecting') : t('chat.connection.disconnected')}
        </div>
      )}

      {/* Right Panel - Agent Info (collapsible) */}
      <AgentInfoPanel
        agent={agent}
        isVisible={isInfoPanelVisible}
        onClose={handleToggleInfo}
        onAgentChange={setAgent}
      />
    </div>
  );
};
